import { createTheme } from "@mui/material/styles";
import { colors } from "./colors";
import { typography } from "./typography";

// Premium color palette with sophisticated gradients and effects
export const premiumColors = {
  ...colors,
  premium: {
    // Sophisticated gradients
    gradients: {
      hero: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      card: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
      button: "linear-gradient(135deg, #a53c49 0%, #8b2635 100%)",
      background: "radial-gradient(ellipse at top, #f8fafc 0%, #e2e8f0 100%)",
      accent: "linear-gradient(135deg, #d4af37 0%, #b8860b 100%)",
      glass:
        "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
    },
    // Sophisticated shadows
    shadows: {
      subtle: "0 2px 8px rgba(0, 0, 0, 0.08)",
      medium: "0 8px 24px rgba(0, 0, 0, 0.12)",
      dramatic: "0 20px 40px rgba(0, 0, 0, 0.15)",
      glow: "0 0 20px rgba(165, 60, 73, 0.3)",
      glass: "0 8px 32px rgba(0, 0, 0, 0.1)",
    },
    // Glass morphism effects
    glass: {
      background: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
    },
    // Accent colors for premium feel
    accent: {
      gold: "#d4af37",
      platinum: "#e5e4e2",
      copper: "#b87333",
    },
  },
  // Animation configuration
  animations: {
    durations: {
      fast: 200,
      normal: 300,
      slow: 500,
      dramatic: 800,
    },
    easings: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      bounce: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
      elastic: "cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    },
  },
} as const;

// Enhanced typography with premium styling
export const premiumTypography = {
  ...typography,
  // Display typography for hero sections
  display: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 300,
    letterSpacing: "-0.02em",
    lineHeight: 1.1,
  },
  // Premium headline styling
  headline: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 300,
    letterSpacing: "-0.01em",
    lineHeight: 1.2,
  },
  // Enhanced caption styling
  caption: {
    ...typography.caption,
    fontWeight: 500,
    letterSpacing: "0.05em",
    textTransform: "uppercase" as const,
  },
} as const;

// Create premium theme
export const premiumTheme = createTheme({
  palette: {
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
    text: {
      primary: colors.primary.main,
      secondary: colors.neutral.dark,
    },
    grey: {
      50: "#fafafa",
      100: colors.neutral.light,
      200: colors.neutral.medium,
      300: colors.neutral.dark,
      400: colors.primary.light,
      500: colors.primary.main,
    },
  },

  typography: premiumTypography,

  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },

  spacing: 8,

  // Enhanced component overrides with premium styling
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          fontWeight: 400,
          color: colors.primary.main,
          fontSize: "16px",
          "@media (max-width: 600px)": {
            fontSize: "16px",
          },
        },
        "*:focus-visible": {
          outline: `2px solid ${colors.secondary.main}`,
          outlineOffset: "2px",
        },
        // Smooth scrolling for better animation experience
        html: {
          scrollBehavior: "smooth",
        },
        // Hardware acceleration for animations
        "*": {
          willChange: "auto",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none" as const,
          fontWeight: 500,
          minHeight: 44,
          minWidth: 44,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:focus-visible": {
            outline: `2px solid ${colors.secondary.main}`,
            outlineOffset: "2px",
          },
          // Premium hover effects
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: premiumColors.premium.shadows.medium,
          },
        },
        contained: {
          background: premiumColors.premium.gradients.button,
          boxShadow: premiumColors.premium.shadows.subtle,
          "&:hover": {
            background: premiumColors.premium.gradients.button,
            boxShadow: premiumColors.premium.shadows.dramatic,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: premiumColors.premium.shadows.subtle,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: premiumColors.premium.shadows.medium,
          },
          "@media (max-width: 600px)": {
            marginBottom: 16,
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 16,
          paddingRight: 16,
          "@media (max-width: 600px)": {
            paddingLeft: 12,
            paddingRight: 12,
          },
          "@media (min-width: 1200px)": {
            paddingLeft: 24,
            paddingRight: 24,
          },
        },
      },
    },
  },
});

export default premiumTheme;
